import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import BaseModal from '../../components/BaseModal/BaseModal';
import { S, Icon } from '../regureStyles';
import toast from 'react-hot-toast';

const ReportClaimModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const userLogin = useSelector(state => state.userLogin);
  // Tenant-configured claim-type vocabulary. Empty array means the tenant
  // hasn't populated it — fall back to the legacy hardcoded list so
  // existing tenants (ARGOS etc.) don't see a broken dropdown.
  const tenantClaimTypes = useSelector(state => state.tenantConfig?.config?.claimTypes) || [];
  const claimTypeOptions = tenantClaimTypes.length > 0
    ? tenantClaimTypes.map(v => ({ value: v, label: v }))
    : [
        { value: 'daily_sickness',          label: t('screens.portal.lobDailySickness') },
        { value: 'professional_liability',  label: t('screens.portal.lobProfessionalLiability') },
        { value: 'legal_protection',        label: t('screens.portal.lobLegalProtection') },
        { value: 'property',                label: t('screens.portal.lobProperty') },
      ];
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [policies, setPolicies] = useState([]);
  const [formData, setFormData] = useState({
    policyId: '',
    lineOfBusiness: '',
    lossDate: '',
    priority: 'medium',
    description: '',
    reportedBy: 'claimant',
  });

  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (userLogin?.userInfo?.status === 'success' && open) {
      fetchPolicies();
    }
  }, [userLogin, open, apiUrl]);

  const fetchPolicies = async () => {
    try {
      const response = await Axios.get(`${apiUrl}/me/my-policies`, { withCredentials: true });
      setPolicies(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch policies:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addFiles = fileList => {
    const arr = Array.from(fileList);
    setPendingFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...arr.filter(f => !existingNames.has(f.name))];
    });
  };

  const removeFile = name => setPendingFiles(prev => prev.filter(f => f.name !== name));

  const handleSubmit = async () => {
    if (!formData.policyId) {
      toast.error(t('screens.portal.errSelectPolicy'));
      return;
    }
    if (!formData.lossDate) {
      toast.error(t('screens.portal.errLossDate'));
      return;
    }
    if (!formData.description) {
      toast.error(t('screens.portal.errDescription'));
      return;
    }

    setLoading(true);
    try {
      // Create form data with files
      const form = new FormData();
      form.append('policyId', formData.policyId);
      form.append('lineOfBusiness', formData.lineOfBusiness);
      form.append('lossDate', formData.lossDate);
      form.append('priority', formData.priority);
      form.append('lossDescription', formData.description);
      form.append('reportedBy', formData.reportedBy);

      pendingFiles.forEach(file => {
        form.append('files', file);
      });

      const response = await Axios.post(`${apiUrl}/me/my-claims`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('screens.portal.claimSubmitted'));
      onClose();
      onSuccess && onSuccess(response.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.message || t('screens.portal.failedSubmitClaim'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setPendingFiles([]);
    setFormData({
      policyId: '',
      lineOfBusiness: '',
      lossDate: '',
      priority: 'medium',
      description: '',
      reportedBy: 'claimant',
    });
    onClose();
  };

  return (
    <BaseModal
      showModal={open}
      onClose={handleClose}
    >
      <div
        style={{
          width: '90%',
          maxWidth: 700,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 40,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: S.textPrimary }}>
              {t('screens.portal.reportClaimTitle')}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 6 }}>
              {t('screens.portal.reportClaimStep', { step })}
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${S.borderLight}`,
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: S.textMuted,
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = S.bg;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#fff';
            }}
          >
            ✕
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
          {[1, 2, 3].map(s => (
            <div
              key={s}
              style={{
                flex: 1,
                height: 4,
                background: s <= step ? S.burgundy : S.borderLight,
                borderRadius: 2,
                transition: 'background 0.3s'
              }}
            />
          ))}
        </div>

        {/* Step 1: Policy & Loss Details */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
                {t('screens.portal.policyLabel')}
              </label>
              <select
                value={formData.policyId}
                onChange={e => handleChange('policyId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 10,
                  fontSize: 14,
                  color: S.textPrimary,
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  fontWeight: 500
                }}
              >
                <option value=''>{t('screens.portal.policyPlaceholder')}</option>
                {policies.map(p => (
                  <option key={p._id} value={p._id}>
                    {p.policyNumber} — {p.lineOfBusiness?.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
                {t('screens.portal.lineOfBusinessLabel')}
              </label>
              <select
                value={formData.lineOfBusiness}
                onChange={e => handleChange('lineOfBusiness', e.target.value)}
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 10,
                  fontSize: 14,
                  color: S.textPrimary,
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  fontWeight: 500
                }}
              >
                <option value=''>{t('screens.portal.lineOfBusinessPlaceholder')}</option>
                {claimTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
                  {t('screens.portal.lossDateLabel')}
                </label>
                <input
                  type='date'
                  value={formData.lossDate}
                  onChange={e => handleChange('lossDate', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 10,
                    fontSize: 14,
                    color: S.textPrimary,
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    fontWeight: 500
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
                  {t('screens.portal.priorityLabel')}
                </label>
                <select
                  value={formData.priority}
                  onChange={e => handleChange('priority', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 10,
                    fontSize: 14,
                    color: S.textPrimary,
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    fontWeight: 500
                  }}
                >
                  <option value='low'>{t('screens.portal.priorityLow')}</option>
                  <option value='medium'>{t('screens.portal.priorityMedium')}</option>
                  <option value='high'>{t('screens.portal.priorityHigh')}</option>
                  <option value='critical'>{t('screens.portal.priorityCritical')}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Description */}
        {step === 2 && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
                {t('screens.portal.descriptionLabel')}
              </label>
              <textarea
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                placeholder={t('screens.portal.descriptionPlaceholder')}
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 10,
                  fontSize: 14,
                  color: S.textPrimary,
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  minHeight: 160,
                  resize: 'vertical',
                  fontWeight: 500
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
                {t('screens.portal.reportedByLabel')}
              </label>
              <select
                value={formData.reportedBy}
                onChange={e => handleChange('reportedBy', e.target.value)}
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 10,
                  fontSize: 14,
                  color: S.textPrimary,
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  fontWeight: 500
                }}
              >
                <option value='claimant'>{t('screens.portal.reportedByClaimant')}</option>
                <option value='broker'>{t('screens.portal.reportedByBroker')}</option>
                <option value='third_party'>{t('screens.portal.reportedByThirdParty')}</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 3: Documents */}
        {step === 3 && (
          <div>
            <input
              ref={fileInputRef}
              type='file'
              multiple
              accept='.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png'
              style={{ display: 'none' }}
              onChange={e => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              style={{
                border: `2px dashed ${dragOver ? S.burgundy : S.borderLight}`,
                borderRadius: 14,
                padding: '48px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragOver ? 'rgba(128,0,32,0.03)' : 'transparent',
                transition: 'all 0.2s',
                marginBottom: 24
              }}
            >
              <Icon name="upload" color={dragOver ? S.burgundy : '#94a3b8'} size={32} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary, marginTop: 8 }}>{t('screens.portal.dropDocsHere')}</div>
              <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{t('screens.portal.dropDocsHint')}</div>
            </div>

            {pendingFiles.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pendingFiles.map(f => (
                  <div
                    key={f.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 14px',
                      background: S.bg,
                      border: `1px solid ${S.borderLight}`,
                      borderRadius: 10,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Icon name="file-text" color={S.burgundy} size={18} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{f.name}</div>
                        <div style={{ fontSize: 11, color: S.textMuted }}>{(f.size / 1024).toFixed(1)} KB</div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(f.name)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: S.textMuted,
                        fontSize: 18,
                        padding: 0
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 40 }}>
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              disabled={loading}
              style={{
                flex: 1,
                padding: '11px 18px',
                border: `1px solid ${S.borderLight}`,
                background: '#fff',
                color: S.textSecondary,
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: loading ? 0.6 : 1
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = S.bg;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#fff';
              }}
            >
              {t('screens.portal.previous')}
            </button>
          )}
          {step < 3 && (
            <button
              onClick={() => setStep(step + 1)}
              disabled={loading || (step === 1 && !formData.policyId)}
              style={{
                flex: 1,
                padding: '11px 18px',
                background: S.burgundy,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: (loading || (step === 1 && !formData.policyId)) ? 0.6 : 1
              }}
              onMouseEnter={e => {
                if (!loading && (step !== 1 || formData.policyId)) {
                  e.currentTarget.style.background = '#5c001a';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = S.burgundy;
              }}
            >
              {t('screens.portal.next')}
            </button>
          )}
          {step === 3 && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                flex: 1,
                padding: '11px 18px',
                background: loading ? S.textMuted : S.burgundy,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
              onMouseEnter={e => {
                if (!loading) {
                  e.currentTarget.style.background = '#5c001a';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = S.burgundy;
              }}
            >
              {loading ? (
                <>
                  <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                  {t('screens.portal.submitting')}
                </>
              ) : (
                <>
                  <Icon name="check" color="#fff" size={16} />
                  {t('screens.portal.submitClaim')}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </BaseModal>
  );
};

export default ReportClaimModal;
